import { Campaign } from '@shared/types';
import { campaigns as baseCampaigns } from '@shared/data/portfolioData';
import { BACKEND_CONFIG, FIREBASE_CONFIG } from '../config';
import { firestoreConfigProvider } from './persistence/FirestoreConfigProvider';

export interface DiscountResult {
    originalPrice: number;
    discountedPrice: number;
    discountAmount: number;
    appliedCampaign: Campaign | null;
}

const STORAGE_KEY = 'campaign_config';

/**
 * Service to handle dynamic discount calculations based on active campaigns.
 */
class DiscountServiceClass {
    private campaigns: Campaign[] = [];
    private initialized = false;

    private async loadData() {
        if (this.initialized) return;

        let savedCampaigns: Campaign[] | null = null;

        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                const data = await firestoreConfigProvider.getCampaigns();
                if (data && data.length > 0) savedCampaigns = data;
            } catch (e) {
                console.error('Failed to load campaigns from Firestore', e);
            }
        } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
            try {
                const res = await fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/discounts`);
                const data = await res.json();
                if (data && data.length > 0) savedCampaigns = data;
            } catch (e) {
                console.error('Failed to load campaigns from backend', e);
            }
        }

        if (!savedCampaigns) {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                try {
                    savedCampaigns = JSON.parse(saved);
                } catch (e) { }
            }
        }

        this.campaigns = savedCampaigns && savedCampaigns.length > 0 ? savedCampaigns : [...baseCampaigns];
        this.initialized = true;
    }

    private async saveConfig(): Promise<void> {
        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                await firestoreConfigProvider.saveCampaigns(this.campaigns);
            } catch (e) {
                console.error('Failed to save campaigns to Firestore', e);
            }
        } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
            try {
                await fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/discounts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.campaigns)
                });
            } catch (e) {
                console.error('Failed to save campaigns to backend', e);
            }
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.campaigns));
        }
    }

    /**
     * Get all currently active campaigns
     */
    public getActiveCampaigns(): Campaign[] {
        const now = new Date();
        return this.campaigns.filter(campaign => {
            if (campaign.isActive === false) return false;

            if (campaign.startDate && new Date(campaign.startDate) > now) return false;
            if (campaign.endDate && new Date(campaign.endDate) < now) return false;

            return true;
        });
    }

    /**
     * Calculate price after applying the best suitable discount.
     * Note: This is synchronous for UI performance, assuming loadData() 
     * was called at app start or campaigns are already available.
     */
    public calculateDiscount(basePrice: number, serviceId?: string, tierId?: string): DiscountResult {
        const activeCampaigns = this.getActiveCampaigns();
        let bestCampaign: Campaign | null = null;
        let maxReduction = 0;

        for (const campaign of activeCampaigns) {
            let matches = false;

            // Check if campaign applies to this specific service
            if (serviceId && campaign.serviceIds.includes(serviceId)) {
                matches = true;
            }

            // Check if campaign applies to this membership tier
            if (tierId && campaign.targetTierIds?.includes(tierId)) {
                matches = true;
            }

            // If no specific service or tier is provided, it might be a general campaign
            if (!serviceId && !tierId && (!campaign.serviceIds.length && !campaign.targetTierIds?.length)) {
                matches = true;
            }

            if (matches) {
                let reduction = 0;
                if (campaign.discountPercentage) {
                    reduction = basePrice * (campaign.discountPercentage / 100);
                } else if (campaign.discountAmount) {
                    reduction = campaign.discountAmount;
                }

                if (reduction > maxReduction) {
                    maxReduction = reduction;
                    bestCampaign = campaign;
                }
            }
        }

        const discountedPrice = Math.max(0, basePrice - maxReduction);

        return {
            originalPrice: basePrice,
            discountedPrice,
            discountAmount: maxReduction,
            appliedCampaign: bestCampaign
        };
    }

    public async refresh(): Promise<void> {
        this.initialized = false;
        await this.loadData();
    }

    public getCampaignsSync(): Campaign[] {
        return this.campaigns;
    }

    public async updateCampaign(campaign: Campaign): Promise<void> {
        const index = this.campaigns.findIndex(c => c.id === campaign.id);
        if (index !== -1) {
            this.campaigns[index] = campaign;
        } else {
            this.campaigns.push(campaign);
        }
        await this.saveConfig();
    }
}

export const DiscountService = new DiscountServiceClass();
// Initialize immediately (non-blocking)
DiscountService.refresh().catch(console.error);
